import type { DraftRecord, HistoryEntry, ImageRecord, Row } from '../types'

const DRAFTS_KEY = 'gestor-v2-drafts'
const HISTORY_KEY = 'gestor-v2-history'
const IMAGES_KEY = 'gestor-v2-images'
const ACADEMY_KEY = 'gestor-v2-academy'
const SETTINGS_KEY = 'gestor-v2-settings'

function read<T>(key: string, fallback: T): T {
  try {
    const value = localStorage.getItem(key)
    return value ? JSON.parse(value) as T : fallback
  } catch {
    return fallback
  }
}

function write<T>(key: string, value: T): void {
  localStorage.setItem(key, JSON.stringify(value))
}

export function listDrafts(): DraftRecord[] { return read<DraftRecord[]>(DRAFTS_KEY, []) }
export function saveDraft(sectionId: string, title: string, row: Row): DraftRecord {
  const drafts = listDrafts()
  const id = `${sectionId}:${row.id || row.student_id || row.catalog_id || crypto.randomUUID()}`
  const record: DraftRecord = { id, sectionId, title, row, updatedAt: new Date().toISOString() }
  write(DRAFTS_KEY, [record, ...drafts.filter((draft) => draft.id !== id)].slice(0, 100))
  return record
}
export function removeDraft(id: string): void { write(DRAFTS_KEY, listDrafts().filter((draft) => draft.id !== id)) }
export function clearDrafts(): void { write(DRAFTS_KEY, []) }
export function replaceDrafts(records: DraftRecord[]): void { write(DRAFTS_KEY, records.slice(0, 100)) }

export function listLocalHistory(): HistoryEntry[] { return read<HistoryEntry[]>(HISTORY_KEY, []) }
export function addLocalHistory(entry: HistoryEntry): void { write(HISTORY_KEY, [entry, ...listLocalHistory()].slice(0, 1000)) }
export function clearLocalHistory(): void { write(HISTORY_KEY, []) }
export function replaceLocalHistory(records: HistoryEntry[]): void { write(HISTORY_KEY, records.slice(0, 1000)) }

export function listLocalImages(): ImageRecord[] { return read<ImageRecord[]>(IMAGES_KEY, []) }
export function saveLocalImage(record: ImageRecord): void {
  write(IMAGES_KEY, [record, ...listLocalImages().filter((image) => image.id !== record.id)].slice(0, 500))
}
export function deleteLocalImage(id: string): void { write(IMAGES_KEY, listLocalImages().filter((image) => image.id !== id)) }
export function replaceLocalImages(records: ImageRecord[]): void { write(IMAGES_KEY, records.slice(0, 500)) }

export function academyProgress(): Record<string, boolean> { return read<Record<string, boolean>>(ACADEMY_KEY, {}) }
export function setAcademyProgress(id: string, done: boolean): void { write(ACADEMY_KEY, { ...academyProgress(), [id]: done }) }

export function readSettings(): Record<string, string | boolean> { return read<Record<string, string | boolean>>(SETTINGS_KEY, {}) }
export function writeSettings(settings: Record<string, string | boolean>): void { write(SETTINGS_KEY, settings) }

const DB_NAME = 'gestor-pedagogico-v2'
const STORE = 'image-blobs'
function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)
    request.onupgradeneeded = () => request.result.createObjectStore(STORE)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}
export async function saveImageBlob(id: string, blob: Blob): Promise<void> {
  const db = await openDb()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).put(blob, id)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
  db.close()
}
export async function getImageBlob(id: string): Promise<Blob | null> {
  const db = await openDb()
  const result = await new Promise<Blob | null>((resolve, reject) => {
    const request = db.transaction(STORE, 'readonly').objectStore(STORE).get(id)
    request.onsuccess = () => resolve((request.result as Blob | undefined) ?? null)
    request.onerror = () => reject(request.error)
  })
  db.close()
  return result
}
export async function deleteImageBlob(id: string): Promise<void> {
  const db = await openDb()
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).delete(id)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
  db.close()
}
